import { Alerts } from "../../../../../utils/Alerts.js";
import { useToggle } from "../../../../../hooks/useToggle.js";
import { DateTime as DateTime$1 } from "../../../../../components/DateTime/index.js";
import { useRPCContext } from "../../../../../contexts/RPCContext.js";
import { useLocation } from "../../../../Locations/hooks/useLocation.js";
import { IdLabel } from "../../../../../components/IdLabel/index.js";
import { LocationName } from "../../../../Locations/shared/LocationName.js";
import { UserAnPCheck } from "../../../../shared/UserAnPCheck.js";
import { useDownloadsApiServiceContext } from "../../../../../contexts/DownloadsApiServiceContext.js";
import { Tags } from "../../../../Tags/shared/Tags.js";
import { Priority } from "../../../../../components/Priority/Priority.js";
import { EventTypeTitle } from "../../EventTypeTitle.js";
import { EditEventDetailsForm } from "./components/EditEventDetailsForm.js";
import { DownloadEventsOptionsModal } from "./components/DownloadEventsOptionsModal.js";
import dayjs from "dayjs";
import { useEffect } from "react";
import { ContextMenu, CopyText, Icon, Label, toastManager, useModalContext } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { downloadBase64AsPdf, formatAddress } from "@therms/web-js";
import { MdRefresh } from "react-icons/md";
//#region src/modules/Events/shared/EventInteractive/EventDetails/index.tsx
function EventDetails({ allowEdit, event, eventType, refresh }) {
	const { rpcService } = useRPCContext();
	const { client } = useDownloadsApiServiceContext();
	const [isEditing, toggleIsEditing] = useToggle(false);
	const [isPickingReferences, toggleIsPickingReferences] = useToggle(false);
	const modalCtx = useModalContext();
	const { data: locationData } = useLocation({ locationId: event.locationId });
	const handleDownloadEventPDF = (opts) => {
		const toastId = Alerts.info(/* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(Icon, {
			Icon: MdRefresh,
			spin: true
		}), " Starting download, please wait..."] }), {
			autoClose: false,
			position: "top-center"
		});
		client.events.DownloadEvent({ body: {
			eventId: event.id,
			showEventsRefs: opts.showEventsRefs,
			showInternalContent: opts.showInternalContent,
			showLocationsRefs: opts.showLocationsRefs,
			showLogEntriesRefs: opts.showLogEntriesRefs,
			showRecordsRefs: opts.showRecordsRefs,
			showReportsRefs: opts.showReportsRefs,
			showUsersRefs: opts.showUsersRefs,
			withComments: opts.withComments,
			withRefHistory: opts.withRefHistory
		} }, { timeout: 6e4 }).then((data) => {
			const result = data;
			if (result.pdfBase64) downloadBase64AsPdf(`${dayjs(event.date).format()} ${event.title} ${event.subTitle}`, result.pdfBase64);
		}).catch((err) => {
			console.error("err downloading report", err);
		}).finally(() => toastManager.dismiss(toastId));
	};
	const handleReopenEvent = () => {
		rpcService.Events.ReopenEvent.v1.call({ eventId: event.id }).then(() => {
			Alerts.info("Event reopened");
			refresh?.();
		}).catch((err) => {
			Alerts.error("Error reopening Event", err.message);
		});
	};
	const handleDeleteEvent = () => {
		rpcService.Events.DeleteEvent.v1.call({ eventId: event.id }).then(() => {
			Alerts.info("Event deleted");
			modalCtx?.closeHandler?.();
		}).catch((err) => {
			Alerts.error("Error deleting Event", err.message);
		});
	};
	const title = /* @__PURE__ */ jsxs("div", {
		className: "flex w-full grow flex-wrap-reverse items-center justify-between text-lg",
		children: [/* @__PURE__ */ jsxs("div", {
			className: "gap-x-xxs flex items-baseline",
			children: [
				/* @__PURE__ */ jsx(EventTypeTitle, { eventType }),
				" ",
				/* @__PURE__ */ jsx(IdLabel, {
					className: "traking-wide text-color-subtle text-base font-bold",
					idLabel: event.idLabel
				})
			]
		}), /* @__PURE__ */ jsxs("div", {
			className: "gap-sm flex",
			children: [/* @__PURE__ */ jsx(Priority, { priority: event.priority }), /* @__PURE__ */ jsx(UserAnPCheck, {
				check: (user) => user.anp.role.ops.access.canDownloadAndExportData || user.anp.role.dispatch.access.canDownloadAndExportData || (user.anp.role.ops.permissions.createEvents || user.anp.role.dispatch.permissions.createEvents) && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.dispatch.permissions.editEvents || event?.assignedUsers.find(({ userId }) => user.id === userId || user.anp.role.ops.permissions.canReopenEvents || user.anp.role.dispatch.permissions.canReopenEvents || user.anp.role.dispatch.permissions.editEvents || user.anp.role.ops.permissions.editEvents),
				children: /* @__PURE__ */ jsxs(ContextMenu, { children: [
					/* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => user.anp.role.dispatch.access.canDownloadAndExportData || user.anp.role.ops.access.canDownloadAndExportData,
						children: /* @__PURE__ */ jsx(ContextMenu.Option, {
							onClick: toggleIsPickingReferences,
							children: "Download PDF"
						})
					}),
					allowEdit && /* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => (user.anp.role.ops.permissions.createEvents || user.anp.role.dispatch.permissions.createEvents) && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.dispatch.permissions.editEvents || event?.assignedUsers.find(({ userId }) => user.id === userId),
						children: /* @__PURE__ */ jsx(ContextMenu.Option, {
							onClick: toggleIsEditing,
							children: "Edit Details"
						})
					}),
					/* @__PURE__ */ jsx(ContextMenu.Divider, {}),
					/* @__PURE__ */ jsxs(UserAnPCheck, {
						check: (user) => user.anp.role.ops.permissions.canReopenEvents || user.anp.role.dispatch.permissions.canReopenEvents,
						children: [/* @__PURE__ */ jsx(ContextMenu.Option, {
							onClick: handleReopenEvent,
							children: "Reopen Event"
						}), /* @__PURE__ */ jsx(ContextMenu.Divider, {})]
					}),
					/* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => user.anp.role.ops.permissions.deleteEvents || user.anp.role.dispatch.permissions.deleteEvents,
						children: /* @__PURE__ */ jsx(ContextMenu.Option, {
							onClick: handleDeleteEvent,
							children: "Delete"
						})
					})
				] })
			})]
		})]
	});
	useEffect(() => {
		if (modalCtx?.setHeader) modalCtx.setHeader(title);
	}, [modalCtx]);
	if (isEditing) return /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(EditEventDetailsForm, {
		event,
		eventType,
		onCancel: toggleIsEditing,
		onComplete: () => {
			refresh?.();
			toggleIsEditing();
		}
	}) });
	return /* @__PURE__ */ jsxs("div", {
		className: "gap-y-sm flex flex-col",
		children: [
			!modalCtx?.setHeader && title,
			/* @__PURE__ */ jsxs("div", {
				className: "gap-sm inline-flex w-full flex-wrap justify-between",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "flex flex-col",
					children: [
						/* @__PURE__ */ jsx(Label, { children: "Location" }),
						/* @__PURE__ */ jsxs("span", {
							className: "text-color-strong gap-x-xxs flex",
							children: [/* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(LocationName, { locationId: event.locationId }) }), event.subLocationId && /* @__PURE__ */ jsxs("span", {
								className: "gap-x-xxs flex",
								children: [/* @__PURE__ */ jsx("span", { children: "/" }), /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(LocationName, { locationId: event.subLocationId }) })]
							})]
						}),
						!!locationData?.location?.address && /* @__PURE__ */ jsx(CopyText, {
							value: formatAddress(locationData.location.address),
							children: /* @__PURE__ */ jsx("span", {
								className: "font-light",
								children: formatAddress(locationData.location.address, true)
							})
						})
					]
				}), /* @__PURE__ */ jsxs("div", {
					className: "flex flex-col",
					children: [/* @__PURE__ */ jsx(Label, { children: "Time" }), /* @__PURE__ */ jsx("div", {
						className: "gap-x-sm inline-flex",
						children: /* @__PURE__ */ jsx("span", {
							className: "text-color-strong",
							children: /* @__PURE__ */ jsx(DateTime$1, {
								relative: true,
								timestamp: event.date
							})
						})
					})]
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				className: "gap-x-sm inline-flex justify-between",
				children: Array.isArray(event.activityTypeTags) && !!event.activityTypeTags.length && /* @__PURE__ */ jsx("div", {
					className: "grow",
					children: /* @__PURE__ */ jsx(Tags, { tags: event.activityTypeTags })
				})
			}),
			isPickingReferences && /* @__PURE__ */ jsx(DownloadEventsOptionsModal, {
				onDownload: handleDownloadEventPDF,
				closeHandler: toggleIsPickingReferences
			})
		]
	});
}
//#endregion
export { EventDetails };

//# sourceMappingURL=index.js.map